/**
 * Bulk Stock / Bulk Move: the list of scanned items held on the handheld until one zone is scanned
 * for all of them. Pure, so what the list accepts, replaces and sends is pinned by tests.
 *
 * The server decides whether a scan may join the list (`bulkScanCheck`: status, packaging overlap
 * against the list) and re-checks every item when the zone is assigned (`bulkScanAssign`, all or
 * nothing). What is decided here is only what the handheld already knows: a barcode scanned twice,
 * the list's size limits, one location per list, and — once a destination is chosen — which listed
 * items already sit in it.
 */
import { scannedLotVariables } from '@/lib/scanning/scannedLot';

export type BulkScanMode = 'STOCK' | 'MOVE';

/** Mirrors the server's BULK_SCAN_MAX_ITEMS / BULK_SCAN_MAX_UNITS (bulk-scan-assign.service.ts). */
export const BULK_SCAN_MAX_ITEMS = 200;
export const BULK_SCAN_MAX_UNITS = 5000;

/** What was scanned — sent back as-is on assign (an SSCC is its 17 digits with no GTIN). */
export type BulkScanInput = {
  serialNumber: string;
  gtin: string | null;
  lot: string | null;
  /** ISO calendar date (`YYYY-MM-DD`) from AI 17. */
  expiration: string | null;
};

/** `bulkScanCheck`'s accepted item. */
export type BulkScanItem = {
  serializedItemId: string;
  serialNumber: string;
  gtin?: string | null;
  serializedItemType: string;
  status: string;
  lot?: string | null;
  expiration?: string | number | null;
  unitCount: number;
  productCount: number;
  productTitle?: string | null;
  ndc?: string | null;
  zoneId?: string | null;
  zoneName?: string | null;
  locationId?: string | null;
  purchaseOrderNumber?: string | null;
  absorbsSerializedItemIds: string[];
};

export type BulkListItem = BulkScanItem & { scan: BulkScanInput };

export type BulkScanNotice = { title: string; message: string };

const TYPE_LABELS: Record<string, string> = {
  SSCC: 'Pallet',
  CA: 'Case',
  PK: 'Pack',
  ITEM: 'Unit'
};

export const typeLabel = (serializedItemType: string | null | undefined) =>
  TYPE_LABELS[serializedItemType || ''] || 'Item';

/** "Case 0123…", "Unit ABC". */
export const itemLabel = (item: Pick<BulkScanItem, 'serializedItemType' | 'serialNumber'>) =>
  `${typeLabel(item.serializedItemType)} ${item.serialNumber}`;

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "Amoxicillin 500mg", "3 products", or null when the server named none. */
export const productLine = (item: BulkScanItem): string | null => {
  if (item.productCount > 1) return `${item.productCount} products`;
  return item.productTitle?.trim() || item.ndc || null;
};

const sameSerial = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** The list item this barcode already is — the server is not asked about a repeat scan. */
export const findScanned = (list: BulkListItem[], scan: Pick<BulkScanInput, 'serialNumber' | 'gtin'>) =>
  list.find(
    (item) =>
      (sameSerial(item.scan.serialNumber, scan.serialNumber) &&
        (item.scan.gtin || null) === (scan.gtin || null)) ||
      (sameSerial(item.serialNumber, scan.serialNumber) && !!scan.gtin && item.gtin === scan.gtin)
  );

export const duplicateNotice = (item: BulkListItem): BulkScanNotice => ({
  title: 'Already in the list',
  message: `${itemLabel(item)} is already in the list.`
});

export const listTotals = (list: BulkListItem[]) => ({
  itemCount: list.length,
  unitCount: list.reduce((sum, item) => sum + (item.unitCount || 0), 0)
});

/** "3 items · 26 units" */
export const totalsLabel = (list: BulkListItem[]) => {
  const { itemCount, unitCount } = listTotals(list);
  return `${plural(itemCount, 'item')} · ${plural(unitCount, 'unit')}`;
};

/** The one location every listed item is at, or undefined (empty list, or none recorded). */
export const listLocationId = (list: BulkListItem[]): string | undefined =>
  list.find((item) => item.locationId)?.locationId || undefined;

/**
 * Why an accepted item still cannot join this list, or null. Size limits, and one location per
 * list: the zone scanned at the end is one zone, at one location.
 */
export const listRefusal = (list: BulkListItem[], item: BulkScanItem): BulkScanNotice | null => {
  const remaining = list.filter(
    (listed) => !item.absorbsSerializedItemIds.includes(listed.serializedItemId)
  );
  if (remaining.length + 1 > BULK_SCAN_MAX_ITEMS) {
    return {
      title: 'The list is full',
      message: `A bulk list holds at most ${BULK_SCAN_MAX_ITEMS} scans. Assign a zone to these first, then start another.`
    };
  }
  const units = listTotals(remaining).unitCount + item.unitCount;
  if (units > BULK_SCAN_MAX_UNITS) {
    return {
      title: 'Too many units',
      message: `${itemLabel(item)} would bring the list to ${units} units; one bulk action takes at most ${BULK_SCAN_MAX_UNITS}. Assign a zone to these first.`
    };
  }
  const locationId = listLocationId(remaining);
  if (locationId && item.locationId && item.locationId !== locationId) {
    return {
      title: 'Different location',
      message: `${itemLabel(item)} is at a different location than the items already in the list. A bulk list goes into one zone, at one location.`
    };
  }
  return null;
};

/**
 * Add an accepted item. A container replaces the listed items inside it (they move with it);
 * `absorbed` is what it replaced, for the operator to be told.
 */
export const addToList = (list: BulkListItem[], item: BulkScanItem, scan: BulkScanInput) => {
  const absorbedIds = new Set(item.absorbsSerializedItemIds);
  const absorbed = list.filter((listed) => absorbedIds.has(listed.serializedItemId));
  return {
    // Newest first: the row the operator just scanned is the one they look for.
    list: [{ ...item, scan }, ...list.filter((listed) => !absorbedIds.has(listed.serializedItemId))],
    absorbed
  };
};

export const removeFromList = (list: BulkListItem[], serializedItemId: string) =>
  list.filter((item) => item.serializedItemId !== serializedItemId);

/** What the list says once an item was added. */
export const addedNotice = (item: BulkScanItem, absorbed: BulkListItem[]): BulkScanNotice | null =>
  absorbed.length
    ? {
        title: `${typeLabel(item.serializedItemType)} replaces ${plural(absorbed.length, 'item')}`,
        message: `${itemLabel(item)} holds ${absorbed
          .map(itemLabel)
          .join(', ')}, already in the list. They are now counted through it.`
      }
    : null;

/** Listed items already in `zoneId` — a Move there is refused for each. */
export const itemsInZone = (list: BulkListItem[], zoneId: string) =>
  list.filter((item) => item.zoneId && item.zoneId === zoneId);

export const alreadyInZoneNotice = (inZone: BulkListItem[], zoneName: string): BulkScanNotice => ({
  title: `Already in ${zoneName}`,
  message: `${inZone.map(itemLabel).join(', ')} ${
    inZone.length === 1 ? 'is' : 'are'
  } already in ${zoneName}. Remove ${
    inZone.length === 1 ? 'it' : 'them'
  } from the list to move the rest there, or choose another zone.`
});

/** `bulkScanAssign`'s `items`, in scan order, each with its barcode's lot / expiration. */
export const assignItems = (list: BulkListItem[]) =>
  [...list].reverse().map(({ scan }) => ({
    serialNumber: scan.serialNumber,
    gtin: scan.gtin || null,
    ...scannedLotVariables(scan)
  }));

// ───────────────────────────── the draft (survives leaving the screen) ─────────────────────────────

const DRAFT_PREFIX = 'rxerp.bulkScan.v1';
/** A list left untouched this long is not offered back. */
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The list as it stood, kept on this device the way Zone Count keeps a count: a reload, the nav
 * menu or the back button loses nothing. The items are the server's answers at scan time; the
 * assign re-checks every one, so a resumed list can never stock or move something that changed.
 */
export type BulkScanDraft = {
  v: 1;
  mode: BulkScanMode;
  updatedAt: number;
  list: BulkListItem[];
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const draftKey = (companyId: string, mode: BulkScanMode) =>
  `${DRAFT_PREFIX}:${companyId}:${mode}`;

export const saveDraft = (
  storage: StorageLike,
  companyId: string,
  mode: BulkScanMode,
  list: BulkListItem[],
  now: number
) => {
  try {
    const draft: BulkScanDraft = { v: 1, mode, updatedAt: now, list };
    storage.setItem(draftKey(companyId, mode), JSON.stringify(draft));
  } catch {
    // Storage full or unavailable: the list carries on, it just will not survive leaving.
  }
};

export const loadDraft = (
  storage: StorageLike,
  companyId: string,
  mode: BulkScanMode,
  now: number
): BulkScanDraft | null => {
  try {
    const raw = storage.getItem(draftKey(companyId, mode));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (draft?.v !== 1 || draft.mode !== mode || !Array.isArray(draft.list) || !draft.list.length) {
      return null;
    }
    if (now - Number(draft.updatedAt || 0) > DRAFT_MAX_AGE_MS) return null;
    return draft as BulkScanDraft;
  } catch {
    return null;
  }
};

export const clearDraft = (storage: StorageLike, companyId: string, mode: BulkScanMode) => {
  try {
    storage.removeItem(draftKey(companyId, mode));
  } catch {
    // Nothing to clear.
  }
};

export const MODE_COPY: Record<
  BulkScanMode,
  { title: string; verb: string; past: string; zoneTitle: string; zoneHint: string }
> = {
  STOCK: {
    title: 'Bulk Stock',
    verb: 'Stock',
    past: 'Stocked',
    zoneTitle: 'Scan stock zone',
    zoneHint: 'Scan the label on the zone they are going into.'
  },
  MOVE: {
    title: 'Bulk Move',
    verb: 'Move',
    past: 'Moved',
    zoneTitle: 'Scan destination zone',
    zoneHint: 'Scan the label on the zone they are moving into.'
  }
};
